import { useEffect, useRef, useState, useMemo, useCallback, useContext } from "react";
import moment from 'moment';
import { useIntl, getLocale, useSelector, useParams, shallowEqual } from 'umi';
// import { getTimeZone } from "../services/dashboard";
import { findPageSelector } from '@/pages/dashboard/components/NewFilter/util';
import { LANGUAGE_EN } from "../_config/constant";
import { FilterContext } from "@/pages/dashboard/components/PageContainer/context";
import { formatExcelString, getDimensionOption, localeKeyMap } from "./utils";
import { getLanguage, selectValByCondition } from "@home/utils/common";
import { getAiSummary } from "../services/dashboard";
import { IAiSummaryConfig, IAiTableData } from "@/pages/dashboard/pages/changeAnalysis/components/ChangeAnalysisAiTable/type";
import { ISeparatedOption } from "@/pages/dashboard/pages/overview/components/OverviewMetricsChart/type";
import type {
  IFetchParamsFieldMapItem,
  IFetchParamsRootState,
  ISelectArrObjRootState,
  ISelectorMapRootState,
  ITimeZoneRootState,
} from "./customHook.types";
import { getAlignmentAiDataKeyField } from './aiAlignmentUtils';

// 组件挂载，只要防止代码中有很多[]
export const useMount = (callback: () => void, params: any[] = []) => {
  useEffect(() => {
    callback();
  }, params);
};

/**
 * 防重复请求第一层处理，第二层兜底处理在axios请求中
 * @param callback为页面请求函数
 * @param params为useeffect的依赖项
 */
export const useReq = (callback: () => void, params: string[] | object[] = []) => {
  useEffect(() => {
    const timeout = setTimeout(() => {
      callback();
    }, 300);
    return () => clearTimeout(timeout);
  }, params);
};

// 防抖 接口请求专用
export const useDebounce = <V>(value: V, delay?: number) => {
  const [debouncedValue, setDebouncedValue] = useState(value);
  useEffect(() => {
    // 每次在value变化以后，设置一个定时器
    const timeout = setTimeout(() => setDebouncedValue(value), delay);
    // 每次在上一个useEffect处理完以后再运行
    return () => clearTimeout(timeout);
  }, [value, delay]);
  return debouncedValue;
};

/**
 * 返回组件的挂载状态，如果还没挂载或者已经卸载，返回false；反之，返回true
 */
export const useMountedRef = () => {
  const mountedRef = useRef(false);
  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  });
  return mountedRef;
};

interface State<D> {
  error: Error | null;
  data: D | null;
  stat: "idle" | "loading" | "error" | "success";
}

const defaultInitialState: State<null> = {
  stat: "loading",
  data: null,
  error: null,
};

export const useRequest = <D>(initialState?: State<D>) => {
  const [state, setState] = useState<State<D>>({
    ...defaultInitialState,
    ...initialState,
  });

  const setData = (data: D) =>
    setState({
      data,
      stat: "success",
      error: null,
    });

  const setError = (error: Error) =>
    setState({
      error,
      stat: "error",
      data: null,
    });

  // sendRequest 用来触发异步请求
  const sendRequest = (promise: Promise<D>, isPromiseAll = false) => {
    if (!promise?.then) {
      throw new Error("请传入 Promise 类型数据");
    }
    setState({ ...state, stat: "loading" });
    return promise
      // 接口返回结构多样：单请求时为业务数据对象；Promise.all 时为响应数组；此处用 unknown 做入口，再按 isPromiseAll 分支窄化
      .then((data: unknown) => {
        let errorData: { msg?: string; } | null | unknown;
        let isError = false;
        if (isPromiseAll) {
          const errorDataArr = (data as unknown[])?.filter((item: unknown) => !item);
          isError = errorDataArr?.length > 0;
          errorData = isError ? errorDataArr?.[0] : null;
        } else {
          isError = !data;
          errorData = isError ? data : null;
        }
        setState({
          data: data as D,
          stat: isError ? "error" : 'success',
          error: isError ? ((errorData as { msg?: string; })?.msg ?? null) as unknown as Error : null,
        });
        return data as D;
      })
      .catch((error) => {
        setError(error);
        return error;
      });
  };

  return {
    isIdle: state.stat === "idle",
    isLoading: state.stat === "loading",
    isError: state.stat === "error",
    isSuccess: state.stat === "success",
    sendRequest,
    setData,
    setError,
    ...state,
  };
};


/**
 * 返回map筛选的值
 */
export const useMapData = (dataList, attr, paramArr) => {
  const dataArr = useMemo(() => dataList?.map(item => item?.[attr] || '') || [], paramArr);
  return dataArr;
};

/**
 * 获取时区封装封装
 * @params gameCode：游戏id
 */
export const useTimeZone = (getLocalText: (arg0: string, arg1: {}, arg2: boolean) => any) => {
  // const [timeZone, setTimeZone] = useState('');

  // 仅读取 dashboardSetting.gameConfig.time_zone；使用 ITimeZoneRootState 局部类型替代 any
  const { time_zone: timeZone = '+0' } = useSelector((state: ITimeZoneRootState) => {
    const { gameConfig } = state?.dashboardSetting || {};
    return gameConfig || {};
  });

  let timeZoneText = `${getLocalText('realtime_users_time_zone', {}, false)} (UTC${timeZone})`;

  // useEffect(() => {
  //   // alert(gameCode)
  //   gameCode && getTimeZone({ game_code: gameCode }).then((res) => {
  //     const timeZone = res?.time_zone
  //     setTimeZone(timeZone || '+0')
  //   }).catch((err) => {
  //     console.log('err-timezone', err)
  //   })
  // }, [])

  if (typeof getLocalText !== 'function') {
    timeZoneText = `Timezone (UTC${timeZone ?? ''})`;
  }

  return {
    timeZoneText,
    timeZone: timeZone ? Number(timeZone) : ''
  };
};


/**
 * tab切换
 * @params callback：设置tab的函数
 * @params activeId：tab的id
 */
export const useTabChange = (callback: (arg0: any) => void, activeId: string) => {
  useEffect(() => {
    callback(activeId);
  }, [activeId]);
};

export const useLocal = (namespace = '', delimiter = '_') => {
  // const [prefix] = useState(namespace)
  const { formatMessage } = useIntl();
  const locale = getLocale();

  const getLocalText = (id: string, config = {}, isConcat = true) => {
    if(!id){
      return id;
    }

    const messageId = (() => {
      if (isConcat) {
        return namespace ? `${namespace}${delimiter}${id}` : id;
      }
      return id;
    })();

    return formatMessage({
      id: messageId
    }, config);
  };

  return {
    locale,
    isEN: locale === 'en-US',
    isCH: locale === 'zh-CN',
    getLocalText,
    formatMessage
  };
};

/**
 * @param {boolean|undefined} isSpecialFilter
 * isSpecialFilter 是指特殊页面，没有针对页面path配置筛选器的场景
 * 如果特殊页面配置了筛选器，isSpecialFilter则为false
 */
export const useSelectArrObj = (formVal:any, isSpecialFilter?:boolean|undefined, isHideAccumulated?:boolean|undefined, pageDefaultSelector?:any, pagePath?:string, useFrontFilterConfig?:boolean, dimensionInfoMap?: any, dimensionOptions?: (string | Record<string, any>)[] | Record<string, any>, options?: {
  currentSegment?: string;
}) => {
  const { formatMessage } = useIntl();
  const locale = getLocale();
  const isEn = locale === LANGUAGE_EN;
  const {
    currentSegment,
  } = options || {};

  // review
  // DVA 根状态只读取 dashboardSetting/dashboard 两个 model 的子字段，使用 ISelectArrObjRootState 替代 any
  const { gameConfig, dataFrame } = useSelector((state: ISelectArrObjRootState) => ({
    gameConfig: state?.dashboardSetting?.gameConfig,
    dataFrame: state?.dashboard?.currentGame?.data_frame,
  }), shallowEqual);

  const getSelectArr = (formVal:any,gameConfig:any) => {
    let selectArr: ISeparatedOption[] = [];

    const optionsMap: Record<string, unknown> = {};
    const optionsOrderArr: string[] = [];
    if (Array.isArray(dimensionOptions)) {
      dimensionOptions
        ?.filter(d => !d?.excludedMetrics?.includes(currentSegment))
        ?.forEach((opt, index) => {
          const key = typeof opt === 'string' ? opt : opt.value;
          optionsMap[key] = typeof opt === 'string' ? { order: index } : Object.assign({}, opt, { order: index });
          optionsOrderArr.push(key);
        });
    }

    const getMergedOption = (key: string, defaultOption) => {
      const override = optionsMap[key];
      if (!override) return defaultOption;

      const {
        textEn, textCn,
        infoEn, infoCn,
        ...rest
      } = override;

      const text = getLanguage(isEn, textEn, textCn) || defaultOption.text;
      const info = getLanguage(isEn, infoEn, infoCn) || defaultOption.info;

      return Object.assign({}, defaultOption, rest, { text, info });
    };

    const firstItem = getMergedOption('accumulated', {
      groupKey: 'accumulated',
      value: 'accumulated',
      text: formatMessage({ id: 'common_Accumulated' }),
    });
    !isHideAccumulated && selectArr.push(firstItem);

    const isForced = (key: string) => {
      const cfg = optionsMap[key];
      // 有 insertIndex 的项不走内置分支，交给最后统一处理
      return cfg && typeof cfg.insertIndex !== 'number';
    };

    if (formVal?.zone || isForced('zone')) {
      selectArr.push(getMergedOption('zone',
        {
          groupKey: 'separated',
          value: 'zone',
          text: formatMessage({ id: 'common_by_Zone' }),
          info: getLanguage(isEn, dimensionInfoMap?.zone?.en, dimensionInfoMap?.zone?.cn) || '',
        }
      ));
    }

    if (formVal?.region || isForced('region')) {
      selectArr.push(getMergedOption('region',
        {
          groupKey: 'separated',
          value: 'region',
          text: formatMessage({ id: 'common_by_Region' }),
          info: getLanguage(isEn, dimensionInfoMap?.region?.en, dimensionInfoMap?.region?.cn) || '',
        }
      ));
    }
    if (formVal?.region || isForced('country')) {
      selectArr.push(getMergedOption('country',
        {
          groupKey: 'separated',
          value: 'country',
          text: formatMessage({ id: 'common_by_Country' }),
          info: getLanguage(isEn, dimensionInfoMap?.country?.en, dimensionInfoMap?.country?.cn) || '',
        }
      ));
    }
    if (formVal?.channel || isForced('channel')) {
      selectArr.push(getMergedOption('channel',
        {
          groupKey: 'separated',
          value: 'channel',
          text: formatMessage({ id: 'common_by_Channel' }),
          info: getLanguage(isEn, dimensionInfoMap?.channel?.en, dimensionInfoMap?.channel?.cn) || '',
        }
      ));
    }
    if (formVal?.system || isForced('system')) {
      selectArr.push(getMergedOption('system',
        {
          groupKey: 'separated',
          value: 'system',
          text: formatMessage({ id: 'common_by_System' }),
          info: getLanguage(isEn, dimensionInfoMap?.system?.en, dimensionInfoMap?.system?.cn) || '',
        }
      ));
    }
    if (formVal?.category || isForced('category')) {
      selectArr.push(getMergedOption('category',
        {
          groupKey: 'separated',
          value: 'category',
          text: formatMessage({ id: 'common_by_Category' }),
          info: getLanguage(isEn, dimensionInfoMap?.category?.en, dimensionInfoMap?.category?.cn) || '',
        }
      ));
    }
    if (formVal?.category || isForced('product')) {
      selectArr.push(getMergedOption('product',
        {
          groupKey: 'separated',
          value: 'product',
          text: formatMessage({ id: 'common_by_Product' }),
          info: getLanguage(isEn, dimensionInfoMap?.product?.en, dimensionInfoMap?.product?.cn) || '',
        }
      ));
    }
    if(formVal?.bundle || isForced('bundle')) {
      selectArr.push(getMergedOption('bundle',
        {
          groupKey: 'separated',
          value: 'bundle',
          text: formatMessage({ id: 'common_by_Bundle' }),
          info: getLanguage(isEn, dimensionInfoMap?.bundle?.en, dimensionInfoMap?.bundle?.cn) || '',
        }
      ));
    }
    if (formVal?.language || formVal?.lang || isForced('language') || isForced('lang')) {
      selectArr.push(getMergedOption('language',
        {
          groupKey: 'separated',
          value: 'language',
          text: formatMessage({ id: 'common_by_Language' }),
          info: getLanguage(isEn, dimensionInfoMap?.language?.en, dimensionInfoMap?.language?.cn) || '',
        }
      ));
    }
    if (formVal?.churn_level || isForced('churn_level')) {
      selectArr.push(getMergedOption('churn_level',
        {
          groupKey: 'separated',
          value: 'churn_level',
          text: formatMessage({ id: 'common_by_churn_level' }),
          info: getLanguage(isEn, dimensionInfoMap?.churn_level?.en, dimensionInfoMap?.churn_level?.cn) || '',
        }
      ));
    }
    if (formVal?.ua_network || isForced('ua_network')) {
      selectArr.push(getMergedOption('ua_network',
        {
          groupKey: 'separated',
          value: 'ua_network',
          text: formatMessage({ id: 'common_by_ua_network' }),
          info: getLanguage(isEn, dimensionInfoMap?.ua_network?.en, dimensionInfoMap?.ua_network?.cn) || '',
        }
      ));
    }
    if (formVal?.version || isForced('version')) {
      selectArr.push(getMergedOption('version',
        {
          groupKey: 'separated',
          value: 'version',
          text: formatMessage({ id: 'common_by_version' }),
          info: getLanguage(isEn, dimensionInfoMap?.version?.en, dimensionInfoMap?.version?.cn) || '',
        }
      ));
    }
    if (formVal?.ad_format || isForced('ad_format')) {
      selectArr.push(getMergedOption('ad_format',
        {
          groupKey: 'separated',
          value: 'ad_format',
          text: formatMessage({ id: 'common_by_ad_format' }),
          info: getLanguage(isEn, dimensionInfoMap?.ad_format?.en, dimensionInfoMap?.ad_format?.cn) || '',
        }
      ));
    }
    if (formVal?.campaign || isForced('campaign')) {
      selectArr.push(getMergedOption('campaign',
        {
          groupKey: 'separated',
          value: 'campaign',
          text: formatMessage({ id: 'common_by_campaign' }),
          info: getLanguage(isEn, dimensionInfoMap?.campaign?.en, dimensionInfoMap?.campaign?.cn) || '',
        }
      ));
    }
    if (formVal?.ab_test || isForced('ab_test')) {
      selectArr.push(getMergedOption('ab_test',
        {
          groupKey: 'separated',
          value: 'ab_test',
          text: formatMessage({ id: 'common_by_ab_test' }),
          info: getLanguage(isEn, dimensionInfoMap?.ab_test?.en, dimensionInfoMap?.ab_test?.cn) || '',
        }
      ));
    }
    // if (formVal?.network_type) {
    //   selectArr.push(
    //     {
    //       groupKey: 'separated',
    //       value: 'network_type',
    //       text: formatMessage({ id: 'common_by_network_type' }),
    //       info: getLanguage(isEn, dimensionInfoMap?.network_type?.en, dimensionInfoMap?.network_type?.cn) || '',
    //     }
    //   );
    // }

    // 处理 dimensionOptions 中 formVal 没有的自定义维度，先追加到末尾
    if (dimensionOptions && Array.isArray(dimensionOptions)) {
      dimensionOptions.forEach((opt) => {
        if (typeof opt === 'string' || !opt.value) return;
        if (selectArr.find(item => item.value === opt.value)) return;
        const { value, textEn, textCn, infoEn, infoCn, groupKey: optGroupKey } = opt;
        selectArr.push({
          groupKey: optGroupKey || 'separated',
          value,
          text: getLanguage(isEn, textEn, textCn) || value,
          info: getLanguage(isEn, infoEn, infoCn) || getLanguage(isEn, dimensionInfoMap?.[value]?.en, dimensionInfoMap?.[value]?.cn) || '',
        });
      });
    }

    if(gameConfig && !isSpecialFilter) {
      const beforeFilterArr = [...selectArr];
      selectArr = getDimensionOption(gameConfig, selectArr, dataFrame, pageDefaultSelector, pagePath, useFrontFilterConfig);

      // 如果 dimensionOptions 中有配置，但被 getDimensionOption 过滤掉了，需要手动补回来
      if (dimensionOptions) {
        Object.keys(optionsMap).forEach(key => {
          if (key !== 'accumulated' && !selectArr.find(item => item.value === key)) {
            const target = beforeFilterArr.find(item => item.value === key);
            if (target) {
              selectArr.push(target);
            }
          }
        });
      }

      // 排序
      selectArr.sort((a, b) => {
        if (optionsOrderArr.length > 0) {
          const indexA = optionsOrderArr.indexOf(a.value);
          const indexB = optionsOrderArr.indexOf(b.value);

          // 如果都在 optionsOrderArr 中，按 index 排序
          if (indexA !== -1 && indexB !== -1) return indexA - indexB;
          // 在 optionsOrderArr 中的排在前面
          if (indexA !== -1) return -1;
          if (indexB !== -1) return 1;
        }
        // 默认按代码执行顺序（即 building selectArr 的顺序）
        return 0;
      });

      !isHideAccumulated && !selectArr.find(item => item.value === 'accumulated') && selectArr.unshift(firstItem);
    }

    // 最终：将有 insertIndex 的项移到指定位置
    if (Array.isArray(dimensionOptions)) {
      dimensionOptions.forEach((opt) => {
        if (typeof opt === 'string' || typeof opt.insertIndex !== 'number') return;
        const curIdx = selectArr.findIndex(item => item.value === opt.value);
        if (curIdx !== -1 && curIdx !== opt.insertIndex) {
          const [item] = selectArr.splice(curIdx, 1);
          selectArr.splice(Math.min(opt.insertIndex, selectArr.length), 0, item);
        }
      });
    }

    return selectArr;
  };

  const selectArrObj = useMemo(() => {
    if (formVal?.newFilter) {
      const { filterValue } = formVal;
      return getSelectArr(filterValue,gameConfig);
    }
    return getSelectArr(formVal,gameConfig);
  }, [formVal, gameConfig, getLocale(), currentSegment]);

  return selectArrObj;
};



export const useWeekDayArr = () => {
  const { formatMessage } = useIntl();
  const weekDayArr = useMemo(() => {
    const WEEK_DAY_ARR = [
      formatMessage({ id: 'common_mon' }),
      formatMessage({ id: 'common_tues' }),
      formatMessage({ id: 'common_wed' }),
      formatMessage({ id: 'common_thur' }),
      formatMessage({ id: 'common_fri' }),
      formatMessage({ id: 'common_sat' }),
      formatMessage({ id: 'common_sun' }),
    ];
    return WEEK_DAY_ARR;
  }, [getLocale()]);
  return weekDayArr;
};

export const useWeekDay = () => {
  const { getLocalText } = useLocal('common');

  const WEEK_DAY_ARR = [
    getLocalText('sun'),
    getLocalText('mon'),
    getLocalText('tues'),
    getLocalText('wed'),
    getLocalText('thur'),
    getLocalText('fri'),
    getLocalText('sat'),
  ];

  const getWeekDay = (day) => {
    const dayNum = moment(day).day();
    return WEEK_DAY_ARR[dayNum];
  };

  const getWeekDayStr = (day, type?: string) => {
    const dayStr = getWeekDay(day);
    if (type?.toLowerCase() === 'month' || !dayStr) return day;
    return `${day}(${dayStr})`;
  };

  return {
    getWeekDay,
    getWeekDayStr
  };
};



// 获取屏幕分辨率信息
export const useWindowSize = () => {
  const [windowSize, setWindowSize] = useState({
    width: window.innerWidth,
    height: window.innerHeight,
  });
  useEffect(() => {
    const updateSize = () => setWindowSize({
      width: window.innerWidth,
      height: window.innerHeight,
    });
    window.addEventListener('resize', updateSize);
    return () => window.removeEventListener('resize', updateSize);
  }, []);

  return windowSize;
};

/**
 * @param pagePath 要跳转的相对页面路径, 开头不包含斜杠
 * @return 返回跳转的绝对路径
 */
export function useLinkPath(pagePath: string|string[]): string|string[] {
  const {type, gamecode: id}:any = useParams();
  const urlPrefix = `${type}/${id}`;
  if (Array.isArray(pagePath)) {
    return pagePath.map((path) => `/dashboard/${urlPrefix}/${path}`);
  }
  return `/dashboard/${urlPrefix}/${pagePath}`;

}

/**
 * 获取中英文 options 和 解释
 * @param values
 * @param explainInfo 可选, 如果传递了, 则返回选项解释
 */
export const useLocalOptions = (values: string[] = []) => {
  const { formatMessage } = useIntl();
  return useMemo(() => values.map(value => {
    const text = formatMessage({ id: `common_options_${value}` });
    return {
      value,
      text
    };
  }), [values, getLocale()]);
};

// 获取日周月中英文
export const useTimeTypeVal = (timeRangeNum: number, timeType: string) => {
  const { formatMessage } = useIntl();
  return useMemo(() => {
    const isSingleTime = timeRangeNum <= 1;
    const timeObj = {
      day: isSingleTime ? formatMessage({ id: 'common_day' }) : formatMessage({ id: 'common_days' }),
      week: isSingleTime ? formatMessage({ id: 'common_week' }) : formatMessage({ id: 'common_weeks' }),
      month: isSingleTime ? formatMessage({ id: 'common_month' }) : formatMessage({ id: 'common_months' }),
    };
    return timeObj[timeType?.toLowerCase()];
  }, [getLocale(), timeRangeNum, timeType]);
};

/**
 * 这个方法主要用于 对下拉复选框的值进行排序.
 *
 * @param options 下拉复选框的选项数组
 * @return function 返回一个可以对 value进行排序的函数
 * 参考 ShowMoreStatistics组件 和 MetricsSelect组件
 */
export const useSortValue = (options: any[] = []) => {
  // 根据options获取到每个value对应的下标, 并得到映射对象.
  const valueIndexMapping = useMemo(() => options?.reduce?.((mapping, opt, index) => {
    mapping[opt.value] = index;
    return mapping;
  }, {}), [options]);

  return useCallback((value) =>
    // 根据映射对象进行排序.
    value.sort((a, b) => (valueIndexMapping[a] - valueIndexMapping[b]))
  , [options]);
};

export const useRemoveReqSession = () => {
  useEffect(() => (() => {
    window.sessionStorage.removeItem(`reqNumObj`);
  }));
};

// 原switchChart组件option转换
export const useOption = (option: any) => {
  try {
    // eslint-disable-next-line react-hooks/rules-of-hooks
    const { xAxisData, legendData, lineSeries, barSeries, isNodata } = useMemo(() => {
      // option.chart 元素是 { x, y, name, data, stack } 形态；用 unknown 入口 + 访问时窄化
      const xAxisData = option?.chart?.[0]?.data?.map((item: { x: unknown; }) => item?.x) || [];
      const legendData = option?.chart?.map((item: { name: unknown; }) => item?.name) || [];
      const lineSeries = option?.chart?.map((item: { name: unknown; data: Array<{ y: unknown; }>; }) => ({
        name: item?.name,
        type: 'line',
        data: item?.data?.map(cItem => cItem?.y),
        symbol: 'circle',
      })) || [];
      const barSeries = option?.chart?.map((item: { stack?: unknown; name?: unknown; data: Array<{ y: unknown; }>; }) => {
        // ECharts bar series 字段外层由 ...item 合入，使用 Record<string, unknown> 保留可扩展字段
        const barSeriesObj: Record<string, unknown> = {
          ...item,
          name: item?.name,
          type: 'bar',
          data: item?.data?.map(cItem => cItem?.y),
          barGap: '5%',
        };
        if (item?.stack) barSeriesObj.barCategoryGap = '50%';
        return barSeriesObj;
      }) || [];
      const isNodata = (!option?.chart || option?.chart?.length <= 0) && (!option?.table || !option?.table?.data || option?.table?.data?.length <= 0);
      return { xAxisData, legendData, lineSeries, barSeries, isNodata };
    }, [option]);
    return { xAxisData, legendData, lineSeries, barSeries, isNodata };
  } catch (error) {
    return { xAxisData: [], legendData: [], lineSeries: [], barSeries: [], isNodata: true };
  }
};

// 根据配置返回 对应的中英文文本
export const useLocalText = (config: { id: string; key: string; values?: object; }[] = []): object => {
  const { formatMessage } = useIntl();

  const result:any =  useMemo(() => config.reduce((acc:any, item:any) => {
    acc[item.key] = formatMessage({ id: item.id }, item.values);
    return acc;
  }, {}), [getLocale()]);
  return result;
};

export const useFetchParams = (props: {
  gameConfig: {
    default_selector: any;
    page_selector: any;
  };
  routePath: string;
}) => {
  const { gameConfig, routePath } = props;
  // 仅用到 dashboard.currentGame.data_frame，其他字段忽略；使用 IFetchParamsRootState 替代 any
  const dataFrame = useSelector((state: IFetchParamsRootState) => state?.dashboard?.currentGame?.data_frame || 'mobile');
  const paramsFilter = useCallback(
    (params: { filters: { [x: string]: any; }; accumulation: { [x: string]: any; }; }) => {
      const {
        default_selector: defaultSelector = [],
        page_selector: pageSelector = [],
      } = gameConfig || {};
      // 维度键 → { filter, accumulation } 的静态映射
      const fieldMap: Record<string, IFetchParamsFieldMapItem> = {
        zone: {
          filter: 'zone_list',
          accumulation: ['by_zone'],
        },
        region: {
          filter: 'market_list',
          accumulation: ['by_region', 'by_country'],
        },
        channel: {
          filter: 'channel_list',
          accumulation: ['by_channel'],
        },
        system: {
          filter: 'os_list',
          accumulation: ['by_system'],
        },
        lang: {
          filter: 'lang_list',
          accumulation: ['by_language'],
        },
        language: {
          filter: 'lang_list',
          accumulation: ['by_language'],
        },
        bundle: {
          filter: 'bundle_list',
          accumulation: ['by_bundle'],
        },
      };
      const visibleList = findPageSelector(
        routePath,
        pageSelector,
        defaultSelector,
        dataFrame
      )
        // .filter((item) => {
        //   return item.visible !== 0;
        // })
        ?.map((item: { key?: string; }) => fieldMap[item?.key ?? '']);
      const newFilter = (() => {
        const filter: {
          [key: string]: any;
        } = {};
        for (const key in params?.filters) {
          if (visibleList?.some(item => item?.filter === key)) {
            filter[key] = params.filters[key];
          }
        }
        return filter;
      })();

      const newAccumulation = (() => {
        const accumulation: {
          [key: string]: any;
        } = {};
        for (const key in params?.accumulation) {
          if (key === 'by_time' || key === 'not_by_date' || visibleList?.some(item => item?.accumulation?.includes(key))) {
            accumulation[key] = params?.accumulation[key];
          }
        }
        return Object.keys(accumulation).length > 0 ? accumulation : null;
      })();
      const filterIsNullFlag = JSON.stringify(newFilter) == '{}';
      const hasAccumulation = Boolean(params?.accumulation);

      const result = {
        ...params,
        filters: newFilter,
        accumulation: newAccumulation,
      };

      !hasAccumulation && delete result.accumulation;
      filterIsNullFlag && delete result.filters;
      return result;
    },
    [gameConfig],
  );

  return {
    paramsFilter
  };
};

export const  useForceUpdate = () => {
  const [value, setState] = useState(true);
  return () => setState(!value);
};

// 筛选器数据中英文翻译
export const useSelectorMap = () => {
  const locale = getLocale();
  // 仅用到 dashboardSetting 的 selector 相关字段；使用 ISelectorMapRootState 替代 any
  const {selectorMap, selector, uaSelectorMap, uaSelector} = useSelector((state: ISelectorMapRootState) => state?.dashboardSetting || {},shallowEqual);
  const isEn = locale === LANGUAGE_EN;
  /** 从 FilterContext 获取 extraSelector（如 ad_format），与 Redux selector 合并 */
  const { extraSelector } = useContext(FilterContext) || {};
  const mergedSelector = useMemo(() => {
    if (!extraSelector) return selector;
    return Object.assign({}, selector, extraSelector);
  }, [selector, extraSelector]);

  // 普通页面筛选器
  const getSelectorText = useCallback((regionDataType: string, key: string, isDownload = false) => {
    if (localeKeyMap[key?.toLowerCase()]) {
      if(isDownload === true){
        return formatExcelString(selectValByCondition(isEn,key, localeKeyMap[key.toLowerCase()]));
      }
      return selectValByCondition(isEn,key, localeKeyMap[key.toLowerCase()]);
    }
    if (!mergedSelector) return isDownload === true ? formatExcelString(key) : key;
    const localeMap = selectorMap?.nameEnMap;

    /** custom_filters 维度（如 ad_format）：从原始数组按 code 查找翻译 */
    const rawList = mergedSelector?.[regionDataType];
    if (Array.isArray(rawList)) {
      const item = rawList.find((it: Record<string, string>) => it?.code === key);
      if (item) {
        const text = isEn ? (item?.name_en ?? key) : (item?.name_ch ?? item?.name_en ?? key);
        return isDownload === true ? formatExcelString(text) : text;
      }
    }

    if (!localeMap || isEn ) {
      return isDownload === true ? formatExcelString(key) : key;
    }
    return isDownload === true ? formatExcelString(localeMap?.[regionDataType]?.[key] || key) : localeMap?.[regionDataType]?.[key] || key;
  }, [mergedSelector,locale]);

  // 获取筛选器中英文案(code映射>full_name)
  const getFilterItemText = useCallback((regionDataType: string, code: string, name: string, isDownload = false) => {
    const isEn = locale === LANGUAGE_EN;
    const filterItem = selectorMap?.nameCnMap?.[regionDataType];

    if (filterItem?.[code]) {
      if(isDownload === true){
        return formatExcelString(selectValByCondition(isEn, filterItem[code].en.trim(), filterItem[code].cn.trim()));
      }
      return selectValByCondition(isEn, filterItem[code].en.trim(), filterItem[code].cn.trim());
    }
    // 如果匹配不到code,则用full_name匹配
    if(isDownload === true){
      return getSelectorText(regionDataType, name, isDownload);
    }

    return getSelectorText(regionDataType, name).trim();
  }, [selector, locale, selectorMap]);



  // ua页面筛选器
  const getUaSelectorText = useCallback((regionDataType: string, key: string) => {
    const isEn = locale === LANGUAGE_EN;
    if (localeKeyMap[key?.toLowerCase()]) {
      return isEn? key: localeKeyMap[key.toLowerCase()];
    }
    if (!uaSelector) return key;
    const localeMap = uaSelectorMap;
    if (!localeMap || isEn ) {
      return key;
    }
    return localeMap?.[regionDataType]?.[key] || key;
  }, [uaSelector,locale]);

  /**
   * 维度 code → 图例中英文展示名（与 getSelectorText / getFilterItemText 数据源一致，供折线图 legend 使用）
   */
  const getDimensionLegendEnCn = useCallback(
    (regionDataType: string, code: string) => {
      const k = String(code ?? '');
      const kl = k.toLowerCase();
      if (localeKeyMap[kl]) {
        return { enName: k, cnName: localeKeyMap[kl] };
      }
      const filterItem = selectorMap?.nameCnMap?.[regionDataType];
      if (filterItem?.[k]) {
        const row = filterItem[k];
        return {
          enName: row.en?.trim() || k,
          cnName: row.cn?.trim() || k,
        };
      }
      const rawList = mergedSelector?.[regionDataType];
      if (Array.isArray(rawList)) {
        const item = rawList.find(
          (it: Record<string, string>) => it?.code === k,
        );
        if (item) {
          return {
            enName: item?.name_en ?? k,
            cnName: item?.name_ch ?? item?.name_en ?? k,
          };
        }
      }
      const localeMap = selectorMap?.nameEnMap;
      const cnFromMap = localeMap?.[regionDataType]?.[k];
      if (cnFromMap) {
        return { enName: k, cnName: cnFromMap };
      }
      // 无映射时直接使用接口原始 code，避免再维护一套展示格式化
      const raw = k.trim() || 'unknown';
      return { enName: raw, cnName: raw };
    },
    [mergedSelector, selectorMap],
  );

  return {
    locale,
    getSelectorText,
    getUaSelectorText,
    getFilterItemText,
    getDimensionLegendEnCn,
  };
};

// 经分获取AI总结
export function useAiSummary(params: {
  game_code: string;
  data_key?: string;
  tag: string;
  enabled?: boolean; // 控制是否启用
}) {
  const [content, setContent] = useState<string>('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const { game_code, data_key, tag, enabled = true } = params;

  useEffect(() => {
    // 防止竞态：当依赖变化时，旧请求的回调不再更新状态
    let isStale = false;

    // 如果未启用或 data_key 不存在，重置状态并返回
    if (!enabled || !data_key) {
      setContent('');
      setLoading(false);
      setError(null);
      return;
    }

    setLoading(true);
    setError(null);

    getAiSummary({ game_code, dataKey: data_key, tag })
      .then((data: string) => {
        if (!isStale) setContent(data);
      })
      .catch((err) => {
        if (!isStale) {
          setError(err.message || 'Failed to fetch AI summary');
          setContent('');
        }
      })
      .finally(() => {
        if (!isStale) setLoading(false);
      });

    return () => {
      isStale = true;
    };
  }, [game_code, data_key, tag, enabled]);

  return {
    data: content,
    loading,
    error,
  };
}

export function useMultiAiSummary(params: {
  data: Record<string, IAiTableData | undefined>;
  aiConfig: IAiSummaryConfig[];
  alignmentValue?: string;
}) {
  const {
    aiConfig: aiConfigArr,
    data,
    alignmentValue,
  } = params;
  const [results, setResults] = useState<{ data: string; loading: boolean; error: string | null; }[]>([]);
  const fetchedKeysRef = useRef<Set<string>>(new Set());
  const fetchedResultsRef = useRef<Map<string, string>>(new Map());
  const dataRef = useRef(data);
  const aiConfigRef = useRef(aiConfigArr);
  const alignmentValueRef = useRef(alignmentValue);

  dataRef.current = data;
  aiConfigRef.current = aiConfigArr;
  alignmentValueRef.current = alignmentValue;

  const isAlignmentAiDataKeyEnabled = (aiConfig?: IAiSummaryConfig) => (
    Boolean(aiConfig?.enableAlignmentAiDataKey)
  );

  const getTableAiTag = (aiConfig?: IAiSummaryConfig) => {
    const alignmentTag = alignmentValueRef.current
      ? aiConfig?.alignmentTagMap?.[alignmentValueRef.current]
      : undefined;
    return alignmentTag || aiConfig?.tag;
  };

  const getTableAiDataKey = (
    tableReq?: IAiTableData['req'],
    aiConfig?: IAiSummaryConfig,
  ) => {
    const alignmentField = getAlignmentAiDataKeyField(
      alignmentValueRef.current,
      aiConfig?.alignmentDataKeyMap,
    );
    const alignmentDataKey = isAlignmentAiDataKeyEnabled(aiConfig) && alignmentField
      ? tableReq?.data_keys?.[alignmentField]
      : undefined;
    return alignmentDataKey || tableReq?.data_key;
  };

  const getTableAiSummary = (
    tableReq?: IAiTableData['req'],
    aiConfig?: IAiSummaryConfig,
  ) => {
    const alignmentField = getAlignmentAiDataKeyField(
      alignmentValueRef.current,
      aiConfig?.alignmentDataKeyMap,
    );
    const alignmentDataKey = isAlignmentAiDataKeyEnabled(aiConfig) && alignmentField
      ? tableReq?.data_keys?.[alignmentField]
      : undefined;

    if (!alignmentDataKey) {
      return tableReq?.data_analysis;
    }
    return tableReq?.ai_analysis?.[alignmentField];
  };

  useEffect(() => {
    // 与 useAiSummary 一致：依赖变化时标记旧请求过期，避免对齐切换后旧 Promise 写回 state
    let isStale = false;

    const currentAiConfig = aiConfigRef.current;
    const currentData = dataRef.current;

    if (!currentAiConfig?.length) {
      setResults([]);
      return () => {
        isStale = true;
      };
    }

    const newResults = currentAiConfig.map((aiConfig) => {
      const tableObj = aiConfig?.dataKey ? currentData?.[aiConfig.dataKey] : undefined;
      const { req: tableReq, data: tableData, loading: tableLoading } = tableObj || {};
      const hasData = Array.isArray(tableData) ? tableData?.length > 0 : false;
      const aiSummary = getTableAiSummary(tableReq, aiConfig);
      const aiDataKey = getTableAiDataKey(tableReq, aiConfig);
      const aiTag = getTableAiTag(aiConfig);

      if (aiSummary) {
        return { data: aiSummary, loading: false, error: null };
      }

      if (tableLoading) {
        return { data: '', loading: true, error: null };
      }

      if (!aiTag || !hasData || !aiDataKey) {
        return { data: '', loading: false, error: null };
      }

      const cacheKey = `${aiDataKey}_${aiTag}`;
      if (fetchedResultsRef.current.has(cacheKey)) {
        return { data: fetchedResultsRef.current.get(cacheKey) || '', loading: false, error: null };
      }

      // 请求进行中：始终展示 loading，不读闭包内过期的 results[index]
      if (fetchedKeysRef.current.has(cacheKey)) {
        return { data: '', loading: true, error: null };
      }

      return { data: '', loading: true, error: null };
    });

    setResults(newResults);

    // 记录本次 effect 各 index 对应的 cacheKey，回调时校验防止错位写入
    const expectedCacheKeys = currentAiConfig.map((aiConfig) => {
      const tableObj = aiConfig?.dataKey ? currentData?.[aiConfig.dataKey] : undefined;
      const { req: tableReq, data: tableData, loading: tableLoading } = tableObj || {};
      const hasData = Array.isArray(tableData) ? tableData?.length > 0 : false;
      const aiSummary = getTableAiSummary(tableReq, aiConfig);
      const aiDataKey = getTableAiDataKey(tableReq, aiConfig);
      const aiTag = getTableAiTag(aiConfig);
      if (aiSummary || tableLoading || !aiTag || !hasData || !aiDataKey) {
        return null;
      }
      return `${aiDataKey}_${aiTag}`;
    });

    const fetchPromises = currentAiConfig.map(async (aiConfig, index) => {
      const tableObj = aiConfig?.dataKey ? currentData?.[aiConfig.dataKey] : undefined;
      const { req: tableReq, data: tableData, loading: tableLoading } = tableObj || {};
      const hasData = Array.isArray(tableData) ? tableData?.length > 0 : false;
      const aiSummary = getTableAiSummary(tableReq, aiConfig);
      const aiDataKey = getTableAiDataKey(tableReq, aiConfig);
      const aiTag = getTableAiTag(aiConfig);

      if (aiSummary || tableLoading || !aiTag || !hasData || !aiDataKey) {
        return null;
      }

      const cacheKey = `${aiDataKey}_${aiTag}`;
      if (fetchedKeysRef.current.has(cacheKey)) {
        return null;
      }

      fetchedKeysRef.current.add(cacheKey);

      try {
        const gameCode = currentData?.gameCode;

        if (typeof gameCode !== 'string' || !gameCode) {
          throw new Error('useMultiAiSummary: Missing gameCode');
        }

        const responseData = await getAiSummary({
          game_code: gameCode,
          dataKey: aiDataKey,
          tag: aiTag
        });
        if (isStale) {
          fetchedKeysRef.current.delete(cacheKey);
          return null;
        }
        fetchedResultsRef.current.set(cacheKey, responseData);
        return { index, cacheKey, data: responseData, loading: false, error: null };
      } catch (err) {
        fetchedKeysRef.current.delete(cacheKey);
        if (isStale) {
          return null;
        }
        return { index, cacheKey, data: '', loading: false, error: (err as Error).message || 'Failed to fetch AI summary' };
      }
    });

    Promise.all(fetchPromises).then((fetchResults) => {
      if (isStale) {
        return;
      }
      setResults((prev) => {
        const updated = [...prev];
        fetchResults.forEach((result) => {
          if (!result) {
            return;
          }
          // 对齐/数据已切换时，丢弃与当前 effect 不一致的回调
          if (expectedCacheKeys[result.index] !== result.cacheKey) {
            return;
          }
          updated[result.index] = {
            data: result.data,
            loading: result.loading,
            error: result.error,
          };
        });
        return updated;
      });
    });

    return () => {
      isStale = true;
      // 筛选/对齐切换导致 effect 重跑时，释放 in-flight 标记，避免新 effect 误判「请求进行中」而永久 loading
      expectedCacheKeys.forEach((cacheKey) => {
        if (cacheKey) {
          fetchedKeysRef.current.delete(cacheKey);
        }
      });
    };
  }, [data, alignmentValue]);

  const combinedData = results.map((r) => r.data).filter(Boolean).join('\n');
  const isLoading = results.some((r) => r.loading) || aiConfigRef.current.some((aiConfig) => {
    const tableObj = aiConfig?.dataKey ? dataRef.current?.[aiConfig.dataKey] : undefined;
    const { loading: tableLoading } = tableObj || {};
    return tableLoading;
  });
  const errors = results.map((r) => r.error).filter(Boolean);

  return {
    results,
    data: combinedData,
    loading: isLoading,
    errors,
  };
}
